import {ChangeDetectorRef, Component, inject, Inject, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";
import {CheckInService} from "../../services/check-in.service";
import { HttpResponse } from "@angular/common/http";
import {StatusesService} from "../../services/statuses.service";
import {RequirementsService} from "../../services/requirements.service";
import {UserService} from "../../services/user.service";
import {first, last} from "rxjs/operators";
import { FieldsService } from "../../admin/services/fields.service";
import moment from 'moment';
import { AdminService } from '../../admin/services/admin.service';
import { User } from '../../models/user';

@Component({
    selector: 'app-order-history',
    templateUrl: './order-history.component.html',
    styleUrls: ['./order-history.component.scss'],
    standalone: false
})
export class OrderHistoryComponent implements OnInit {

  changes = signal(undefined);

  private statuses = [];

  private requirements = [];

  private fields = [];

  IGNORE_ATTRS = [
    'check_in_time',
    'custom_params',
    'ended_at',
    'sign_url',
    'updated_at',
    'type',
    'work_id',
    'roles_id',
    'created_at',
    'company_id',
    'document_id',
    'document_type',
  ];

  REDIRECTABLE_ATTRS = {object_id: 'Vehicle' , vehicle_id: 'Vehicle'};

  private data = inject(MAT_DIALOG_DATA);
  private adminService = inject(AdminService);
  private orderService = inject(CheckInService);
  private statusService = inject(StatusesService);
  private requirementsService = inject(RequirementsService);
  private userService = inject(UserService);
  private fieldsService = inject(FieldsService);

  users = signal<User[]>(this.userService.allUsers());

  ngOnInit() {
    this.orderService.getChanges(this.data.order_id).subscribe((value:HttpResponse<any[]>) => {
      this.changes.set(this.formatCustomParams(value.body));
    })

    this.statusService.statuses('Status::Order').pipe(first()).subscribe(value => {
      this.statuses = value;
    });

    this.requirementsService.requirements$.pipe(first()).subscribe(value => {
      this.requirements = value;
    });

    this.fieldsService.all('Field::Order').subscribe((fields) => {
      this.fields = fields;
    });
  }

  filterChangeSet(change){
    const keys = Object.keys(change.changeset).filter((k)=> this.IGNORE_ATTRS.findIndex(a => a ===k) === -1);
    return keys.map(value => {
      return {id: value, name: this.getName(change.item_type, value)};
    });
  }

  formatCustomParams(values:any[]){
    values.forEach((value,index) => {
      const custom = value.changeset['custom_fields'];
      delete value.changeset['custom_fields'];
      if(custom){
        for(const key in custom[0]){
          if(custom[0][key] && custom[1][key]!==custom[0][key]){
            if(!values[index].changeset[key]){
              values[index].changeset[key] = [null,null];
            }
            values[index].changeset[key][0] = custom[0][key];
          }
        }
        for(const key in custom[1]){
          if(custom[1][key] && custom[1][key]!==custom[0][key]){
            if(!values[index].changeset[key]){
              values[index].changeset[key] = [null,null];
            }
            values[index].changeset[key][1] = custom[1][key];
          }
        }
      }
    });
    return values;
  }
  getName(model, key){
    switch (model) {
      case 'Order':
        return this.translateOrderKey(key);
      case 'WorkInstance':
        return this.translateWorkKey(key);
      default:
        return key;
    }
  }

  private translateOrderKey(key) {
    const keyMappings = {
      'booking_date': 'Agendado para',
      'checked_in': 'Recepcionado',
      'client_id': 'Id cliente',
      'comment': 'Comentario',
      'confirmed': 'Confirmado',
      'completed_at': 'Completado',
      'finish_date': 'Fecha finalización',
      'object_id': `Id ${this.adminService.getText('object')}`,
      'requirement_id': 'Requerimiento',
      'status_id': 'Estado',
      'user_id': 'Recepcionista',
      'vehicle_id': 'Id vehiculo'
    };

    return keyMappings[key] || this.getField(key);
  }

  private translateWorkKey(key) {
    const keyMappings = {
      id: 'Id Trabajo',
      user_id: 'Asignado',
      completed_at: 'Completado',
      finished_by: 'Completado por',
      requester_id: 'Solicitante',
      work_time: 'Tiempo de trabajo',
    };

    return keyMappings[key];
  }

  getValue(key:string, id){
    let index;
    switch (key) {
      case 'status_id':
        index = this.statuses.findIndex(s => s.id === id);
        if(index >= 0){
          return this.statuses[index].name;
        }else{
          return id;
        }
      case 'requirement_id':
        index = this.requirements.findIndex(s => s.id === id);
        if(index >= 0){
          return this.requirements[index].name;
        }else{
          return id;
        }
      case 'confirmed':
      case 'checked_in':
        if(id){
          return 'Si';
        }else{
          return 'No';
        }
      case 'requester_id':
      case 'finished_by':
      case 'user_id':
        index = this.users().findIndex(s => s.id === id);
        if(index >= 0){
          return this.users()[index].name;
        }else{
          return id;
        }
      default:
        if(id && id.name){
          return id.name;
        }
        else if(id && id.map){
          return id.map((v) => v.name || v).join(", ");
        }
        else {
          if(key.includes('date')){
            const date = moment(id)
            if(date.isValid()){
              return date.format('YYYY-MM-DD');
            }else{
              return id;
            }
          }
          else if(key.includes('at')) {
            const datetime = moment(id)
            if(datetime.isValid()){
              return datetime.format('YYYY-MM-DD hh:mm A');
            }else{
              return id;
            }
          }
          else{
            return id;
          }
        }
    }
  }

  getModelName(change) {
    let base = this.adminService.getText(change.item_type, 'capitalize');

    if (change.item_type === 'WorkInstance') {
      const workName = this.getWorkName(change.item_id);
      return workName ? `Trabajo: ${workName}` : base;
    }

    return base;
  }

  getWorkName(work_id) {
    return this.data.works.find(w => w.id === work_id)?.name;
  }

  getField(key){
    const index = this.fields.findIndex(s => s.key === key);
    if(index >= 0){
      return this.fields[index].title;
    }else{
      return key;
    }
  }

  getVerb(model, changeset) {
    if (changeset['id']) {
      return `añadió`;
    }
    else if (changeset['checked_in']) {
      return 'recepcionó';
    } 
    else if (changeset['confirmed']) {
      return 'confirmó';
    }
    else if (changeset['completed_at']) {
      if (model === 'Order') {
        return 'finalizó';
      } 
      return 'completó';
    }
    else if (changeset['user_id']) {
      return `modificó ${this.getName(model, 'user_id')?.toLowerCase()} de`;
    }
    else if (changeset['status_id']) {
      return `modificó estado de`;
    }

    return 'modificó';
  }

}
